/* eslint-disable @typescript-eslint/no-explicit-any */
import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { Product, InventoryInfo } from '../types/product';
import { useAuth } from '../auth';
import { addToCart as addToCartAPI, removeFromCart as removeFromCartAPI, updateCartQuantity as updateCartQuantityAPI, clearCart as clearCartAPI, getCart } from '../api/shop';
import { productApi } from '../api/productDetails';
import CartErrorDialog from '../components/main/CartErrorDialog';

export type CartItem = Omit<Partial<Product>, 'inventory'> & { 
  id: number; 
  cartId?: number; 
  quantity: number; 
  variantId?: number; 
  selectedOptions?: { [key: string]: string }; 
  maxAvailable?: number; // Track max available stock for validation
  inStock?: boolean; // Track if product is in stock
  inventory?: InventoryInfo; // Use the proper InventoryInfo type
};

interface CartContextType {
  cartItems: CartItem[];
  cartItemCount: number;
  addToCart: (item: CartItem) => Promise<boolean>;
  removeFromCart: (cartId: number) => void;
  updateQuantity: (cartId: number, quantity: number) => Promise<boolean>;
  clearCart: () => void;
  refreshCartFromAPI: () => Promise<void>;
  updateCartFromAPIResponse: (apiResponse: any) => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const CART_STORAGE_KEY = 'guest_cart_items';

export const CartManagerProvider = ({ children }: { children: ReactNode }) => {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [cartError, setCartError] = useState<{
    success: false;
    message: string;
    error: string;
    availableQuantity?: number;
    locations?: Array<{
      id: number;
      name: string;
      availableQuantity: number;
    }>;
  } | null>(null);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [hasMigratedGuestCart, setHasMigratedGuestCart] = useState(false);
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();

  // Helper function to enrich cart items with fresh stock information
  const enrichCartItemsWithStock = useCallback(async (items: CartItem[]): Promise<CartItem[]> => {
    const enrichedItems = await Promise.all(
      items.map(async (item) => {
        try {
          console.log('CartManager - Fetching fresh stock info for item:', item.id, item.name);
          // Always fetch fresh product details to get current stock information
          const productDetails = await productApi.getProductDetails(item.id);
          
          if (productDetails) {
            let stockInfo: {
              maxAvailable: number;
              inStock: boolean;
              inventory: InventoryInfo;
            } = {
              maxAvailable: 0,
              inStock: false,
              inventory: {
                available: 0,
                inStock: false,
                locations: []
              }
            };
            
            // If item has a variant, prioritize variant stock info
            if (item.variantId && productDetails.variants) {
              const variant = productDetails.variants.find(v => v.id === item.variantId);
              if (variant) {
                console.log('CartManager - Using variant stock info for item:', item.id, 'variant:', item.variantId);
                stockInfo = {
                  maxAvailable: variant.inventory?.available || 0,
                  inStock: variant.inventory?.inStock || false,
                  inventory: {
                    available: variant.inventory?.available || 0,
                    inStock: variant.inventory?.inStock || false,
                    locations: []
                  }
                };
              } else {
                console.warn('CartManager - Variant not found for item:', item.id, 'variantId:', item.variantId);
                // If variant not found, use product stock as fallback
                stockInfo = {
                  maxAvailable: productDetails.inventory?.available || 0,
                  inStock: productDetails.inventory?.inStock || false,
                  inventory: {
                    available: productDetails.inventory?.available || 0,
                    inStock: productDetails.inventory?.inStock || false,
                    locations: productDetails.inventory?.locations || []
                  }
                };
              }
            } else {
              // No variant, use product stock info
              console.log('CartManager - Using product stock info for item:', item.id);
              stockInfo = {
                maxAvailable: productDetails.inventory?.available || 0,
                inStock: productDetails.inventory?.inStock || false,
                inventory: {
                  available: productDetails.inventory?.available || 0,
                  inStock: productDetails.inventory?.inStock || false,
                    locations: productDetails.inventory?.locations || []
                }
              };
            }
            
            console.log('CartManager - Fresh stock info for item:', item.id, stockInfo);
            
            return {
              ...item,
              ...stockInfo
            };
          }
        } catch (error) {
          console.error('CartManager - Failed to fetch stock info for item:', item.id, error);
        }
        
        // Return original item if enrichment fails
        return item;
      })
    );
    
    return enrichedItems;
  }, []);

  // Load from localStorage on mount (for guest users)
  useEffect(() => {
    if (!isAuthenticated) {
      const stored = localStorage.getItem(CART_STORAGE_KEY);
      if (stored) {
        try {
          const parsedItems = JSON.parse(stored);
          setCartItems(parsedItems);
          // Also sync to sessionStorage for consistency
          sessionStorage.setItem('cart_items', JSON.stringify(parsedItems));
          console.log('CartManager - Loaded guest cart from localStorage:', parsedItems);
        } catch (error) {
          console.error('Error parsing guest cart from localStorage:', error);
          // If parsing fails, clear the corrupted data
          localStorage.removeItem(CART_STORAGE_KEY);
          sessionStorage.removeItem('cart_items');
        }
      }
    }
  }, [isAuthenticated]);

  // Persist to localStorage and sessionStorage on change (for guest users)
  useEffect(() => {
    if (!isAuthenticated) {
      console.log('CartManager - Saving guest cart to localStorage and sessionStorage:', cartItems);
      if (cartItems.length > 0) {
        localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartItems));
        sessionStorage.setItem('cart_items', JSON.stringify(cartItems));
      } else {
        // Clear both storages when cart is empty
        localStorage.removeItem(CART_STORAGE_KEY);
        sessionStorage.removeItem('cart_items');
      }
    }
  }, [cartItems, isAuthenticated]);

  const refreshCartFromAPI = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const result = await getCart();

      if (result.items && result.items.length > 0) {
        // Convert API cart items to local cart format
        const apiCartItems: CartItem[] = result.items.map((item: {
          id: number;
          cartId: number;
          productId: number;
          quantity: number;
          variantId?: number;
          selectedOptions?: { [key: string]: string };
          maxAvailable?: number;
          product: {
            name: string;
            price: number;
            imageUrl: string;
            description: string;
            vendor: string;
            inventory?: {
              available: number;
              inStock: boolean;
              locations?: Array<{
                id: number;
                name: string;
                availableQuantity: number;
              }>;
            };
            inStock?: boolean;
          };
        }) => ({
          id: item.productId, // Use productId as the main id for compatibility
          cartId: item.cartId || item.id || Date.now() + Math.random(), // Use cartId from API or generate one
          quantity: item.quantity,
          variantId: item.variantId,
          selectedOptions: item.selectedOptions,
          name: item.product.name,
          price: item.product.price,
          imageUrl: item.product.imageUrl,
          description: item.product.description,
          vendor: item.product.vendor,
          maxAvailable: item.maxAvailable || undefined,
          inStock: item.product?.inventory?.inStock || item.product?.inStock || undefined,
          inventory: item.product?.inventory || undefined
        }));
        
        // Enrich cart items with fresh stock data
        const enrichedCartItems = await enrichCartItemsWithStock(apiCartItems);
        setCartItems(enrichedCartItems);
      } else {
        setCartItems([]);
      }
    } catch (error) {
      console.error('Failed to load cart from API:', error);
      // Keep existing cart items if API fails
    }
  }, [isAuthenticated, enrichCartItemsWithStock]);

  // Handle cart migration and API loading when user logs in
  useEffect(() => {
    const handleUserLogin = async () => {
      if (isAuthenticated && !hasMigratedGuestCart) {
        // First, migrate guest cart to API
        const storedGuestCart = localStorage.getItem(CART_STORAGE_KEY);
        if (storedGuestCart) {
          try {
            const guestCartItems: CartItem[] = JSON.parse(storedGuestCart);
            if (guestCartItems && guestCartItems.length > 0) {
              // Migrate each guest cart item to authenticated user's cart
              const migrationPromises = guestCartItems.map(async (item) => {
                try {
                  await addToCartAPI({
                    productId: item.id,
                    quantity: item.quantity,
                    variantId: item.variantId,
                    selectedOptions: item.selectedOptions
                  });
                  return { success: true, item };
                } catch (error) {
                  return { success: false, item, error };
                }
              });

              // Wait for all migrations to complete
              const migrationResults = await Promise.allSettled(migrationPromises);
              migrationResults.filter(result => 
                result.status === 'fulfilled' && result.value.success
              );

              // Clear guest cart from localStorage after migration
              localStorage.removeItem(CART_STORAGE_KEY);
            }
          } catch (error) {
            console.error('Error processing guest cart migration:', error);
          }
        }
        
        // Mark migration as completed
        setHasMigratedGuestCart(true);
        
        // Then load user cart from API
        await refreshCartFromAPI();
      } else if (isAuthenticated && hasMigratedGuestCart) {
        // User is already logged in and migration completed, just refresh cart
        await refreshCartFromAPI();
      } else if (!isAuthenticated) {
        // User logged out, reset migration flag
        setHasMigratedGuestCart(false);
      }
    };

    handleUserLogin();
  }, [isAuthenticated, hasMigratedGuestCart, refreshCartFromAPI]);

  const addToCart = async (item: CartItem): Promise<boolean> => {
    if (isAuthenticated) {
      try {
        // Validate stock - same logic as ProductDetails
        const maxAvailable = item.maxAvailable;
        const inStock = item.inStock;
        const available = item.inventory?.available;
        
        // First check if product is out of stock
        if (inStock === false) {
          setCartError({
            success: false,
            message: t('cart.error.outOfStock'),
            error: 'INSUFFICIENT_STOCK',
            availableQuantity: 0
          });
          setErrorDialogOpen(true);
          return false; // Failure - out of stock
        }
        
      // Then check if quantity exceeds available stock
      const effectiveMaxAvailable = maxAvailable !== undefined ? maxAvailable : available;
      
      // Check if same product with same variant already exists
      const existingItem = cartItems.find(ci => {
        const idMatch = ci.id === item.id;
        const variantMatch = ci.variantId === item.variantId;
        
        // Compare selectedOptions - both must be null/undefined or both must match
        let optionsMatch = true;
        const ciOptions = ci.selectedOptions || null;
        const itemOptions = item.selectedOptions || null;
        
        if (ciOptions && itemOptions) {
          optionsMatch = JSON.stringify(ciOptions) === JSON.stringify(itemOptions);
        } else if (ciOptions !== itemOptions) {
          optionsMatch = false;
        }
        
        return idMatch && variantMatch && optionsMatch;
      });
      
      // Calculate total quantity (existing + new)
      const totalQuantity = existingItem ? existingItem.quantity + item.quantity : item.quantity;
      
      if (effectiveMaxAvailable !== undefined && effectiveMaxAvailable !== null && totalQuantity > effectiveMaxAvailable) {
        // Set error for stock validation
        const effectiveAvailable = effectiveMaxAvailable - (existingItem?.quantity || 0);
        setCartError({
          success: false,
          message: effectiveMaxAvailable === 0 
            ? t('cart.error.outOfStock')
            : existingItem 
              ? t('cart.error.limitedStockWithCart').replace('{{available}}', effectiveAvailable.toString()).replace('{{inCart}}', existingItem.quantity.toString())
              : t('cart.error.limitedStock').replace('{{count}}', effectiveMaxAvailable.toString()),
          error: 'INSUFFICIENT_STOCK',
          availableQuantity: effectiveAvailable
        });
        setErrorDialogOpen(true);
        return false; // Failure - stock exceeded
      }
        
        // For logged-in users, call API first
        await addToCartAPI({ 
          productId: item.id, 
          quantity: item.quantity,
          variantId: item.variantId,
          selectedOptions: item.selectedOptions
        });
        
        // Refresh cart data to update badge count in header
        await refreshCartFromAPI();
        return true; // Success
      } catch (error: unknown) {
        console.error('Failed to add item to cart via API:', error);
        
        // Check if it's an API error with response data
        if (error && typeof error === 'object' && 'response' in error && (error as any).response?.data) {
          setCartError((error as any).response.data);
          setErrorDialogOpen(true);
          return false; // Failure - API error
        }
        
        // For API errors, don't update local state - let the cart page handle it
        return false; // Failure
      }
    } else {
      // For guest users, validate stock and add item - same logic as ProductDetails
      const maxAvailable = item.maxAvailable;
      const inStock = item.inStock;
      const available = item.inventory?.available;
      
      // First check if product is out of stock
      if (inStock === false) {
        setCartError({
          success: false,
          message: 'This product is currently out of stock',
          error: 'INSUFFICIENT_STOCK',
          availableQuantity: 0
        });
        setErrorDialogOpen(true);
        return false; // Failure - out of stock
      }
      
      // Then check if quantity exceeds available stock
      const effectiveMaxAvailable = maxAvailable !== undefined ? maxAvailable : available;
      
      // Check if same product with same variant already exists
      const existingItem = cartItems.find(ci => {
        const idMatch = ci.id === item.id;
        const variantMatch = ci.variantId === item.variantId;
        
        // Compare selectedOptions - both must be null/undefined or both must match
        let optionsMatch = true;
        const ciOptions = ci.selectedOptions || null;
        const itemOptions = item.selectedOptions || null;
        
        if (ciOptions && itemOptions) {
          optionsMatch = JSON.stringify(ciOptions) === JSON.stringify(itemOptions);
        } else if (ciOptions !== itemOptions) {
          optionsMatch = false;
        }
        
        return idMatch && variantMatch && optionsMatch;
      });
      
      // Calculate total quantity (existing + new)
      const totalQuantity = existingItem ? existingItem.quantity + item.quantity : item.quantity;
      
      if (effectiveMaxAvailable !== undefined && effectiveMaxAvailable !== null && totalQuantity > effectiveMaxAvailable) {
        // Set error for stock validation
        const effectiveAvailable = effectiveMaxAvailable - (existingItem?.quantity || 0);
        setCartError({
          success: false,
          message: effectiveMaxAvailable === 0 
            ? t('cart.error.outOfStock')
            : existingItem 
              ? t('cart.error.limitedStockWithCart').replace('{{available}}', effectiveAvailable.toString()).replace('{{inCart}}', existingItem.quantity.toString())
              : t('cart.error.limitedStock').replace('{{count}}', effectiveMaxAvailable.toString()),
          error: 'INSUFFICIENT_STOCK',
          availableQuantity: effectiveAvailable
        });
        setErrorDialogOpen(true);
        return false; // Failure - stock exceeded
      }
      
      if (existingItem) {
        // Update existing item quantity
        const newQuantity = existingItem.quantity + item.quantity;
        setCartItems(prev => prev.map(ci => {
          const idMatch = ci.id === item.id;
          const variantMatch = ci.variantId === item.variantId;
          
          // Compare selectedOptions - both must be null/undefined or both must match
          let optionsMatch = true;
          const ciOptions = ci.selectedOptions || null;
          const itemOptions = item.selectedOptions || null;
          
          if (ciOptions && itemOptions) {
            optionsMatch = JSON.stringify(ciOptions) === JSON.stringify(itemOptions);
          } else if (ciOptions !== itemOptions) {
            optionsMatch = false;
          }
          
          return idMatch && variantMatch && optionsMatch
            ? { ...ci, quantity: newQuantity, maxAvailable: maxAvailable || ci.maxAvailable } 
            : ci;
        }));
      } else {
        // Add new item to cart with unique cartId for guest users
        const uniqueCartId = Date.now() + Math.random(); // Generate unique ID
        setCartItems(prev => [...prev, { ...item, cartId: uniqueCartId }]);
      }
      return true; // Success
    }
  };

  const removeFromCart = async (cartId: number) => {
    if (isAuthenticated) {
      try {
        // For logged-in users, call API first
        await removeFromCartAPI(cartId);
        
        // Refresh cart data to update badge count in header
        await refreshCartFromAPI();
      } catch (error) {
        console.error('Failed to remove item from cart via API:', error);
        // For API errors, don't update local state - let the cart page handle it
      }
    } else {
      // For guest users, remove by cartId directly
      setCartItems(prev => prev.filter(ci => ci.cartId !== cartId));
    }
  };

  // Update cart items from API response (order-summary or calculate-price)
  const updateCartFromAPIResponse = useCallback((apiResponse: any) => {
    console.log('CartManager - updateCartFromAPIResponse called with:', apiResponse);
    console.log('CartManager - updateCartFromAPIResponse items:', apiResponse?.items);
    
    if (!apiResponse?.items || !Array.isArray(apiResponse.items)) { 
      console.log('CartManager - No items in API response, clearing cart');
      setCartItems([]);
      return;
    }

    // Convert API response items to CartManager format
    const updatedCartItems: CartItem[] = apiResponse.items.map((item: any) => {
      console.log('CartManager - Processing API item:', item);
      console.log('CartManager - API item product:', item.product);
      console.log('CartManager - API item maxAvailable:', item.maxAvailable);
      console.log('CartManager - API item product inventory:', item.product?.inventory);
      
      return {
        // Product fields from API
        ...item.product, // Spread all product fields
        // Override specific fields for cart management
        id: item.productId, // Use productId as the main id
        cartId: item.cartId || item.id || Date.now() + Math.random(), // Use cartId from API or generate one
        quantity: item.quantity,
        variantId: item.variantId,
        selectedOptions: item.selectedOptions,
        maxAvailable: item.maxAvailable || undefined,
        inStock: item.product?.inventory?.inStock || item.product?.inStock || undefined,
        inventory: item.product?.inventory || undefined,
        // Ensure required fields are set
        name: item.product?.name || '',
        price: item.product?.price || item.unitPrice || 0,
        imageUrl: item.product?.imageUrl || '',
        description: item.product?.description || '',
        vendor: item.product?.brandInfo?.name || ''
      };
    });

    console.log('CartManager - Updated cart items from API:', updatedCartItems);
    setCartItems(updatedCartItems);
  }, []);

  const updateQuantity = async (cartId: number, quantity: number): Promise<boolean> => {
    // Validate quantity before making API call
    if (quantity <= 0) {
      console.error('Invalid quantity:', quantity, 'for cart item:', cartId);
      return false;
    }

    if (isAuthenticated) {
      try {
        console.log('Updating quantity for cart item', cartId, 'to', quantity);
        
        // For logged-in users, find the cart item to get variant info
        const cartItem = cartItems.find(ci => ci.cartId === cartId);
        console.log('CartManager - Looking for cartId:', cartId);
        console.log('CartManager - Available cartIds:', cartItems.map(ci => ci.cartId));
        console.log('CartManager - All cart items:', cartItems);
        if (!cartItem) {
          console.error('Cart item not found for cartId:', cartId);
          return false;
        }
        
        // For logged-in users, call API first with variant information
        const response = await updateCartQuantityAPI({ 
          cartId, 
          quantity,
          variantId: cartItem.variantId,
          selectedOptions: cartItem.selectedOptions
        });
        console.log('API response:', response);
        
        // Refresh cart data to update badge count in header
        await refreshCartFromAPI();
        
        return true; // Return success for authenticated users
      } catch (error) {
        console.error('Failed to update cart quantity via API:', error);
        
        // Check if it's an API error with response data
        if (error && typeof error === 'object' && 'response' in error && (error as any).response?.data) {
          setCartError((error as any).response.data);
          setErrorDialogOpen(true);
          return false; // Don't update local state on API error
        }
        
        // For API errors, don't update local state - let the cart page handle it
      }
    } else {
      // For guest users, find the item by cartId and validate stock before updating
      console.log('CartManager (Guest) - Looking for cartId:', cartId);
      console.log('CartManager (Guest) - Available cartIds:', cartItems.map(ci => ci.cartId));
      console.log('CartManager (Guest) - All cart items:', cartItems);
      
      // First, enrich cart items with stock information if missing
      const enrichedCartItems = await enrichCartItemsWithStock(cartItems);
      
      const cartItem = enrichedCartItems.find(ci => ci.cartId === cartId);
      if (cartItem) {
        console.log('CartManager (Guest) - Found cart item:', cartItem);
        console.log('CartManager (Guest) - Cart item stock info:', {
          maxAvailable: cartItem.maxAvailable,
          inStock: cartItem.inStock,
          inventory: cartItem.inventory
        });
        
        // Check if new quantity exceeds available stock - same logic as ProductDetails
        const maxAvailable = cartItem.maxAvailable;
        const inStock = cartItem.inStock;
        const available = cartItem.inventory?.available;
        
        // First check if product is out of stock
        if (inStock === false) {
          setCartError({
            success: false,
            message: t('cart.error.outOfStock'),
            error: 'INSUFFICIENT_STOCK',
            availableQuantity: 0
          });
          setErrorDialogOpen(true);
          return false; // Don't update cart
        }
        
        // Then check if quantity exceeds available stock
        const effectiveMaxAvailable = maxAvailable !== undefined ? maxAvailable : available;
        console.log('CartManager (Guest) - Stock validation:', {
          requestedQuantity: quantity,
          effectiveMaxAvailable,
          maxAvailable,
          available,
          inStock
        });
        
        if (effectiveMaxAvailable !== undefined && effectiveMaxAvailable !== null && quantity > effectiveMaxAvailable) {
          console.error('CartManager (Guest) - Stock validation FAILED:', {
            productName: cartItem.name,
            requestedQuantity: quantity,
            availableQuantity: effectiveMaxAvailable
          });
          // Set error for stock validation
          setCartError({
            success: false,
            message: effectiveMaxAvailable === 0 
              ? t('cart.error.outOfStock')
              : t('cart.error.limitedStock').replace('{{count}}', effectiveMaxAvailable.toString()),
            error: 'INSUFFICIENT_STOCK',
            availableQuantity: effectiveMaxAvailable
          });
          setErrorDialogOpen(true);
          return false; // Don't update cart
        }
        
        console.log('CartManager (Guest) - Stock validation PASSED');
        
        // Update cart items with enriched stock information and new quantity
        setCartItems(prev => {
          const updated = prev.map(ci => {
            if (ci.cartId === cartId) {
              // Find the enriched version of this item
              const enrichedItem = enrichedCartItems.find(eci => eci.cartId === cartId);
              return enrichedItem ? { ...enrichedItem, quantity } : { ...ci, quantity };
            }
            return ci;
          });
          console.log('CartManager - Updated cart items with enriched stock info:', updated);
          return updated;
        });
        return true;
      } else {
        console.log('CartManager (Guest) - Cart item not found for cartId:', cartId);
        return false;
      }
    }

    return false;
  };

  const clearCart = useCallback(async () => {
    setCartItems([]);
    if (isAuthenticated) {
      try {
        // For logged-in users, call API first
        await clearCartAPI();
        
        // Refresh cart data to update badge count in header
        await refreshCartFromAPI();
      } catch (error) {
        console.error('Failed to clear cart via API:', error);
        // For API errors, don't update local state - let the cart page handle it
      }
    }
  }, [isAuthenticated, refreshCartFromAPI]);


  // Calculate total item count for badge
  const cartItemCount = cartItems.reduce((total, item) => total + item.quantity, 0);

  return (
    <CartContext.Provider value={{ 
      cartItems, 
      cartItemCount,
      addToCart, 
      removeFromCart, 
      updateQuantity, 
      clearCart,
      refreshCartFromAPI,
      updateCartFromAPIResponse
    }}>
      {children}
      
      {/* Cart Error Dialog */}
      <CartErrorDialog
        open={errorDialogOpen}
        onClose={() => {
          setErrorDialogOpen(false);
          setCartError(null);
        }}
        error={cartError}
      />
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within a CartManagerProvider');
  return ctx;
}; 